import { Component, Inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { CreateGeonameValue, ReadGeonameValue, UpdateGeonameValue } from '@dasch-swiss/dsp-js';
import { Observable, Subscription } from 'rxjs';
import { BaseValueComponent } from '../base-value.component';
import { ValueErrorStateMatcher } from '../value-error-state-matcher';
import { DisplayPlace, GeonameService, SearchPlace } from '../../services/geoname.service';

export function geonameIdValidator(control: AbstractControl) {
    // null or empty checks are out of this validator's scope
    // check for a valid geoname id object
    const invalid = !(control.value === null || control.value === '' || (typeof control.value === 'object' && 'id' in control.value));
    return invalid ? { invalidType: { value: control.value } } : null;
}

// https://stackoverflow.com/questions/45661010/dynamic-nested-reactive-form-expressionchangedafterithasbeencheckederror
const resolvedPromise = Promise.resolve(null);

@Component({
    selector: 'dsp-geoname-value',
    templateUrl: './geoname-value.component.html',
    styleUrls: ['./geoname-value.component.scss']
})
export class GeonameValueComponent extends BaseValueComponent implements OnInit, OnChanges, OnDestroy {
    @Input() displayValue?: ReadGeonameValue;

    valueFormControl: FormControl;
    commentFormControl: FormControl;

    form: FormGroup;

    valueChangesSubscription: Subscription;
    matcher = new ValueErrorStateMatcher();
    customValidators = [geonameIdValidator];

    $geonameLabel: Observable<DisplayPlace>;

    places: SearchPlace[];

    constructor(@Inject(FormBuilder) private _fb: FormBuilder, private _geonameService: GeonameService) {
        super();
    }

    standardValueComparisonFunc(initValue: { id: string }, curValue: { id: string } | null): boolean {
        return (curValue !== null && typeof curValue === 'object' && 'id' in curValue) && initValue.id === curValue.id;
    }

    getInitValue(): { id: string }  | null {

        if (this.displayValue !== undefined) {
            return {
                id: this.displayValue.geoname
            }; // TODO: try to set a display name to be shown when value is updated
        } else {
            return null;
        }
    }

    /**
     * Used to create a value which is displayed to the user after selection from autocomplete.
     *
     * @param place the user selected place.
     */
    displayPlaceInSearch(place: SearchPlace | null) {
        if (place !== null) {
            return place.displayName;
        }
    }

    ngOnInit() {

        // initialize form control elements
        this.valueFormControl = new FormControl(null);

        this.commentFormControl = new FormControl(null);

        // react to user typing places
        this.valueFormControl.valueChanges.subscribe(
            (searchTerm: string) => {

                // console.log(searchTerm);
                // TODO: move this to a method
                if ((this.mode === 'create' || this.mode === 'update') && searchTerm !== null) {
                    if (typeof searchTerm === 'string' && searchTerm.length >= 3) {
                        // console.log('searching for ' + searchTerm);
                        this._geonameService.searchPlace(searchTerm).subscribe(
                            places => this.places = places,
                            err => this.places = []
                        );
                    } else {
                        this.places = [];
                    }
                }
            }
        );

        this.valueChangesSubscription = this.commentFormControl.valueChanges.subscribe(
            data => {
                this.valueFormControl.updateValueAndValidity();
            }
        );

        this.form = this._fb.group({
            value: this.valueFormControl,
            comment: this.commentFormControl
        });

        this.resetFormControl();

        if (this.mode === 'read') {
            this.$geonameLabel = this._geonameService.resolveGeonameID(this.valueFormControl.value.id);
        }

        resolvedPromise.then(() => {
            // add form to the parent form group
            this.addToParentFormGroup(this.formName, this.form);
        });
    }

    ngOnChanges(changes: SimpleChanges): void {

        // resets values and validators in form controls when input displayValue or mode changes
        // at the first call of ngOnChanges, form control elements are not initialized yet
        this.resetFormControl();

        if (this.mode === 'read' && this.valueFormControl !== undefined) {
            this.$geonameLabel = this._geonameService.resolveGeonameID(this.valueFormControl.value.id);
        }
    }

    ngOnDestroy(): void {
        this.unsubscribeFromValueChanges();

        resolvedPromise.then(() => {
            // remove form from the parent form group
            this.removeFromParentFormGroup(this.formName);
        });
    }

    getNewValue(): CreateGeonameValue | false {

        if (this.mode !== 'create' || !this.form.valid || this.isEmptyVal()) {
            return false;
        }

        const newGeonameValue = new CreateGeonameValue();

        newGeonameValue.geoname = this.valueFormControl.value.id;

        if (this.commentFormControl.value !== null && this.commentFormControl.value !== '') {
            newGeonameValue.valueHasComment = this.commentFormControl.value;
        }

        return newGeonameValue;

    }

    getUpdatedValue(): UpdateGeonameValue | false {

        if (this.mode !== 'update' || !this.form.valid) {
            return false;
        }

        const updatedGeonameValue = new UpdateGeonameValue();

        updatedGeonameValue.id = this.displayValue.id;

        updatedGeonameValue.geoname = this.valueFormControl.value.id;

        if (this.commentFormControl.value !== null && this.commentFormControl.value !== '') {
            updatedGeonameValue.valueHasComment = this.commentFormControl.value;
        }

        return updatedGeonameValue;
    }

}
